<template>
  <div class="student-management-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة شؤون الطلاب</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة طالب جديد
          </el-button>
        </div>
      </template>

      <el-alert
        title="ما هي هذه الواجهة؟"
        type="info"
        description="هذه هي الواجهة الرئيسية لإدارة جميع بيانات الطلاب في المدرسة. يمكنك من هنا إضافة طلاب جدد، تعديل بياناتهم، ونقلهم بين الفصول والأقسام المختلفة."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <div class="filter-section">
        <el-select v-model="filters.study_year_id" placeholder="فلترة حسب السنة الدراسية" clearable @change="handleYearFilterChange" style="width: 240px;">
          <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
        </el-select>
        <el-select v-model="filters.class_id" placeholder="فلترة حسب الفصل" clearable @change="handleClassFilterChange" :disabled="!filters.study_year_id" style="width: 240px;">
          <el-option v-for="classItem in classes" :key="classItem.id" :label="classItem.name" :value="classItem.id" />
        </el-select>
        <el-select v-model="filters.section_id" placeholder="فلترة حسب القسم" clearable @change="fetchStudents" :disabled="!filters.class_id" style="width: 240px;">
          <el-option v-for="section in sections" :key="section.id" :label="section.name" :value="section.id" />
        </el-select>
        <el-input v-model="filters.search" placeholder="ابحث بالاسم أو الرقم..." @input="handleSearch" clearable style="width: 300px;" />
      </div>

      <el-table :data="students" v-loading="loading" border style="width: 100%">
        <el-table-column prop="srn" label="SRN" width="100" />
        <el-table-column prop="name" label="اسم الطالب" />
        <el-table-column prop="class.name" label="الفصل" />
        <el-table-column prop="section.name" label="القسم" />
        <el-table-column label="ولي الأمر">
            <template #default="{ row }">
                {{ row.parent ? `${row.parent.first_name} ${row.parent.last_name}` : 'N/A' }}
            </template>
        </el-table-column>
        <el-table-column label="رقم هاتف ولي الأمر">
            <template #default="{ row }">
                {{ row.parent ? `${row.parent.phone_number_one}` : 'N/A' }}
            </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="220" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-button size="small" type="success" @click="showFinancialRecords(row)">
                <el-icon><Money /></el-icon> المالية
            </el-button>
            <el-popconfirm title="هل أنت متأكد من الحذف؟" @confirm="handleDelete(row.id)">
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-if="pagination.total > 0"
          @current-change="handlePageChange"
          :current-page="pagination.current_page"
          :page-size="pagination.per_page"
          :total="pagination.total"
          layout="total, prev, pager, next"
          background
        />
      </div>
    </el-card>

    <!-- Dialog لإنشاء/تعديل طالب -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="70%" :close-on-click-modal="false">
      <el-steps :active="activeStep" finish-status="success" simple style="margin-bottom: 20px;">
        <el-step title="البيانات الشخصية" :icon="User" />
        <el-step title="بيانات ولي الأمر" :icon="Umbrella" />
        <el-step title="التسجيل الأكاديمي" :icon="School" />
        <el-step title="بيانات إضافية" :icon="More" />
      </el-steps>

      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <!-- Step 1: Personal Details -->
        <div v-if="activeStep === 0">
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item label="الاسم (باللغة الإنجليزية)" prop="name"><el-input v-model="form.name" /></el-form-item>
                    <el-form-item label="الاسم (باللغة العربية)" prop="arabic_name"><el-input v-model="form.arabic_name" /></el-form-item>
                    <el-form-item label="تاريخ الميلاد" prop="date_of_birth"><el-date-picker v-model="form.date_of_birth" type="date" style="width: 100%;" /></el-form-item>
                    <el-form-item label="الجنس" prop="gender">
                        <el-select v-model="form.gender" style="width: 100%;"><el-option label="ذكر" value="male" /><el-option label="أنثى" value="female" /></el-select>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="الصورة الشخصية">
                        <el-upload class="avatar-uploader" action="#" :show-file-list="false" :auto-upload="false" :on-change="handlePhotoChange">
                            <img v-if="photoPreview" :src="photoPreview" class="avatar" />
                            <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
                        </el-upload>
                    </el-form-item>
                </el-col>
            </el-row>
        </div>

        <!-- Step 2: Parent Information -->
        <div v-if="activeStep === 1">
            <el-form-item label="ولي الأمر" prop="parent_id">
                <el-select v-model="form.parent_id" filterable remote :remote-method="searchParents" placeholder="ابحث عن ولي الأمر" style="width: 100%;">
                    <el-option v-for="parent in parents" :key="parent.id" :label="`${parent.first_name} ${parent.last_name}`" :value="parent.id" />
                </el-select>
            </el-form-item>
        </div>

        <!-- Step 3: Academic Info -->
        <div v-if="activeStep === 2">
            <el-form-item label="رقم القيد (SRN)">
                <el-input v-model="form.srn" disabled placeholder="سيتم إنشاؤه تلقائيًا" />
            </el-form-item>
            <el-form-item label="السنة الدراسية" prop="study_year_id">
                <el-select v-model="form.study_year_id" @change="handleFormYearChange" style="width: 100%;">
                    <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
                </el-select>
            </el-form-item>
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item label="الفصل" prop="class_id">
                        <el-select v-model="form.class_id" @change="handleFormClassChange" :disabled="!form.study_year_id" style="width: 100%;">
                            <el-option v-for="cls in formClasses" :key="cls.id" :label="cls.name" :value="cls.id" />
                        </el-select>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item label="القسم" prop="section_id">
                        <el-select v-model="form.section_id" :disabled="!form.class_id" style="width: 100%;">
                            <el-option v-for="sec in formSections" :key="sec.id" :label="sec.name" :value="sec.id" />
                        </el-select>
                    </el-form-item>
                </el-col>
            </el-row>
        </div>
        
        <!-- Step 4: Additional Info -->
        <div v-if="activeStep === 3">
            <el-form-item label="العنوان" prop="address"><el-input v-model="form.address" /></el-form-item>
            <el-form-item label="رقم جواز السفر" prop="passport_num"><el-input v-model="form.passport_num" /></el-form-item>
            <el-form-item label="سقف الشراء اليومي من المقصف (دينار)" prop="daily_allowed_purchase_value"><el-input-number v-model="form.daily_allowed_purchase_value" :min="0" /></el-form-item>
            <el-form-item label="هل استلم الكتب؟" prop="has_books"><el-switch v-model="form.has_books" active-value="yes" inactive-value="no" /></el-form-item>
            <el-form-item label="ملاحظات" prop="note"><el-input v-model="form.note" type="textarea" /></el-form-item>
            <el-form-item label="مفقودات" prop="missing"><el-input v-model="form.missing" type="textarea" /></el-form-item>
        </div>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
            <el-button @click="prevStep" v-if="activeStep > 0">السابق</el-button>
            <el-button type="primary" @click="nextStep" v-if="activeStep < 3">التالي</el-button>
            <el-button type="success" @click="submitForm" :loading="formLoading" v-if="activeStep === 3">
                {{ form.id ? 'تحديث' : 'إنشاء' }}
            </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Dialog لعرض السجلات المالية -->
    <el-dialog v-model="financialDialogVisible" :title="`السجلات المالية للطالب: ${selectedStudent?.name}`" width="70%">
        <el-table :data="studentInvoices" v-loading="financialLoading" border>
            <el-table-column prop="invoice_number" label="رقم الفاتورة" />
            <el-table-column prop="final_amount" label="المبلغ النهائي" />
            <el-table-column prop="paid_amount" label="المبلغ المدفوع" />
            <el-table-column prop="remaining_amount" label="المبلغ المتبقي" />
            <el-table-column prop="status" label="الحالة">
                <template #default="{ row }"><el-tag>{{ row.status }}</el-tag></template>
            </el-table-column>
        </el-table>
    </el-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus, User, School, Umbrella, More, Money } from '@element-plus/icons-vue';
import debounce from 'lodash/debounce';

export default {
  name: 'StudentManagement',
  components: { Plus, ElPopconfirm, User, School, Umbrella, More, Money },
  data() {
    return {
      api: null,
      loading: true,
      students: [],
      studyYears: [],
      classes: [],
      sections: [],
      parents: [],
      formClasses: [],
      formSections: [],
      filters: { study_year_id: null, class_id: null, section_id: null, search: '' },
      pagination: { current_page: 1, per_page: 10, total: 0 },
      dialogVisible: false,
      formLoading: false,
      activeStep: 0,
      form: this.getInitialForm(),
      photoPreview: null,
      
      financialDialogVisible: false,
      financialLoading: false,
      selectedStudent: null,
      studentInvoices: [],

      rules: {
          name: [{ required: true, message: 'الاسم مطلوب', trigger: 'blur' }],
          parent_id: [{ required: true, message: 'ولي الأمر مطلوب', trigger: 'change' }],
          study_year_id: [{ required: true, message: 'السنة الدراسية مطلوبة', trigger: 'change' }],
          class_id: [{ required: true, message: 'الفصل مطلوب', trigger: 'change' }],
          date_of_birth: [{ required: true, message: 'تاريخ الميلاد مطلوب', trigger: 'change' }],
          gender: [{ required: true, message: 'الجنس مطلوب', trigger: 'change' }],
          has_books: [{ required: true, message: 'حقل الكتب مطلوب', trigger: 'change' }],
      }
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل بيانات طالب' : 'إضافة طالب جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchStudents();
    this.fetchFilterData();
  },
  methods: {
    getInitialForm() {
        return { id: null, name: '', arabic_name: '', date_of_birth: '', gender: null, parent_id: null, study_year_id: null, class_id: null, section_id: null, srn: '', address: '', passport_num: '', has_books: 'no', daily_allowed_purchase_value: 0, note: '', missing: '', photo: null };
    },
    async fetchStudents() {
      this.loading = true;
      try {
        const params = { ...this.filters, page: this.pagination.current_page, per_page: this.pagination.per_page };
        const response = await this.api.get('/students', { params });
        this.students = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.current_page = response.data.current_page;
      } catch (error) {
        ElMessage.error('فشل في جلب بيانات الطلاب.');
      } finally {
        this.loading = false;
      }
    },
    async fetchFilterData() {
      try {
        const response = await this.api.get('/study-years');
        this.studyYears = response.data.data || response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب السنوات الدراسية.');
      }
    },
    async handleYearFilterChange() {
      this.filters.class_id = null;
      this.filters.section_id = null;
      this.classes = [];
      this.sections = [];
      if (this.filters.study_year_id) {
        try {
          const response = await this.api.get('/classes', { params: { study_year_id: this.filters.study_year_id } });
          this.classes = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الفصول.');
        }
      }
      this.fetchStudents();
    },
    async handleClassFilterChange() {
      this.filters.section_id = null;
      this.sections = [];
      if (this.filters.class_id) {
        try {
          const response = await this.api.get(`/classes/${this.filters.class_id}/sections-for-assign`);
          this.sections = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الأقسام.');
        }
      }
      this.fetchStudents();
    },
    handleSearch: debounce(function() {
      this.pagination.current_page = 1;
      this.fetchStudents();
    }, 500),
    handlePageChange(page) {
      this.pagination.current_page = page;
      this.fetchStudents();
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.photoPreview = null;
      this.activeStep = 0;
      this.dialogVisible = true;
    },
    async handleEdit(student) {
    try {
        // أولاً: جلب البيانات الكاملة والمحدثة للطالب
        const response = await this.api.get(`/students/${student.id}`);
        const studentData = response.data;

        // ثانياً: تعبئة النموذج بالبيانات التي تم جلبها
        // نستخدم Object.assign للحفاظ على بنية النموذج وتجنب الأخطاء
        this.form = Object.assign(this.getInitialForm(), studentData);
        
        // ثالثاً: تجهيز قائمة أولياء الأمور (كما في الكود السابق)
        if (studentData.parent) {
            this.parents = [studentData.parent];
        }

        // رابعاً (المنطق الجديد ✨): جلب وتجهيز قوائم الفصول والأقسام
        if (studentData.study_year_id) {
            // جلب قائمة الفصول التي تنتمي لنفس السنة الدراسية للطالب
            const classesResponse = await this.api.get('/classes', { params: { study_year_id: studentData.study_year_id } });
            this.formClasses = classesResponse.data;
        }
        
        if (studentData.class_id) {
            // جلب قائمة الأقسام التي تنتمي لنفس فصل الطالب
            const sectionsResponse = await this.api.get(`/classes/${studentData.class_id}/sections-for-assign`);
            this.formSections = sectionsResponse.data;
        }

        // خامساً: تجهيز معاينة الصورة (كما في الكود السابق)
        if (studentData.photo) {
            this.photoPreview = `${process.env.VUE_APP_API_BASE_URL.replace('/api', '')}/storage/${studentData.photo}`;
        } else {
            this.photoPreview = null;
        }

        // أخيراً: عرض النافذة المنبثقة
        this.activeStep = 0;
        this.dialogVisible = true;
        
    } catch (error) {
        ElMessage.error('فشل في جلب بيانات الطالب للتعديل.');
        console.error("Edit error:", error);
    }
},
    async handleDelete(id) {
      try {
        await this.api.delete(`/students/${id}`);
        ElMessage.success('تم حذف الطالب بنجاح.');
        this.fetchStudents();
      } catch (error) {
        ElMessage.error('فشل في حذف الطالب.');
      }
    },
    nextStep() {
      if (this.activeStep < 3) this.activeStep++;
    },
    prevStep() {
      if (this.activeStep > 0) this.activeStep--;
    },
    searchParents: debounce(async function(query) {
      if (query) {
        try {
          const response = await this.api.get('/parents', { params: { search: query } });
          this.parents = response.data.data;
        } catch (error) {
          console.error('Error fetching parents:', error);
        }
      } else {
        this.parents = [];
      }
    }, 300),
    async handleFormYearChange() {
        this.form.class_id = null;
        this.form.section_id = null;
        if(this.form.study_year_id) {
            const response = await this.api.get('/classes', { params: { study_year_id: this.form.study_year_id } });
            this.formClasses = response.data;
        }
    },
    async handleFormClassChange() {
        this.form.section_id = null;
        if(this.form.class_id) {
            const response = await this.api.get(`/classes/${this.form.class_id}/sections-for-assign`);
            this.formSections = response.data;
        }
    },
    handlePhotoChange(file) {
        this.form.photo = file.raw;
        this.photoPreview = URL.createObjectURL(file.raw);
    },
    async submitForm() {
    this.$refs.formRef.validate(async (valid) => {
        if (!valid) {
            ElMessage.error('يرجى التأكد من ملء جميع الحقول المطلوبة.');
            return;
        }

        this.formLoading = true;
        const formData = new FormData();

        // ✨ ---  الحل: تنسيق التاريخ بشكل مضمون --- ✨
        let formattedDateOfBirth = null;
        if (this.form.date_of_birth) {
            // هذه الطريقة تضمن تحويل التاريخ إلى الصيغة الصحيحة سواء كان نصاً أو كائناً
            const date = new Date(this.form.date_of_birth);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedDateOfBirth = `${year}-${month}-${day}`;
        }
        // ✨ --- نهاية الحل --- ✨

        const payload = {
            name: this.form.name,
            arabic_name: this.form.arabic_name,
            date_of_birth: formattedDateOfBirth, // <-- استخدام التاريخ المنسق
            gender: this.form.gender,
            parent_id: this.form.parent_id,
            study_year_id: this.form.study_year_id,
            class_id: this.form.class_id,
            section_id: this.form.section_id,
            address: this.form.address,
            passport_num: this.form.passport_num,
            has_books: this.form.has_books,
            daily_allowed_purchase_value: this.form.daily_allowed_purchase_value,
            note: this.form.note,
            missing: this.form.missing,
        };

        // الآن حلقة المرور أصبحت أبسط
        for (const key in payload) {
            if (payload[key] !== null && payload[key] !== undefined) {
                formData.append(key, payload[key]);
            }
        }
        
        if (this.form.photo && this.form.photo instanceof File) {
            formData.append('photo', this.form.photo);
        }

        try {
            if (this.form.id) {
                formData.append('_method', 'PUT');
                await this.api.post(`/students/${this.form.id}`, formData);
                ElMessage.success('تم تحديث الطالب بنجاح.');
            } else {
                await this.api.post('/students', formData);
                ElMessage.success('تم إضافة الطالب بنجاح.');
            }
            this.dialogVisible = false;
            this.fetchStudents();
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'فشلت العملية.';
            ElMessage.error(errorMsg);
        } finally {
            this.formLoading = false;
        }
    });
},
    async showFinancialRecords(student) {
        this.selectedStudent = student;
        this.financialDialogVisible = true;
        this.financialLoading = true;
        try {
            const response = await this.api.get(`/students/${student.id}/invoices`);
            this.studentInvoices = response.data;
        } catch (error) {
            ElMessage.error('فشل في جلب السجلات المالية.');
        } finally {
            this.financialLoading = false;
        }
    }
  },
};
</script>

<style scoped>
.student-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.filter-section { display: flex; gap: 15px; margin-bottom: 20px; flex-wrap: wrap; }
.pagination-container { display: flex; justify-content: center; margin-top: 20px; }
.dialog-footer { display: flex; justify-content: space-between; width: 100%; }
.avatar-uploader .avatar {
  width: 178px;
  height: 178px;
  display: block;
}
</style>

<style>
.avatar-uploader .el-upload {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}
.avatar-uploader .el-upload:hover {
  border-color: var(--el-color-primary);
}
.el-icon.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
}
</style>
